/**
 * ============================================================================
 * Configuración de Inbound Email — DAYSI (Frontend)
 * ============================================================================
 *
 * Drawer para que el administrador configure el ingreso de emails por cliente:
 *
 *   - alias                 → "acme-7x3" → la dirección será acme-7x3@in.daysi.app
 *   - enabled               → ON/OFF
 *   - domain_whitelist      → ["acme.com", "contadores-acme.com"]
 *   - sender_whitelist      → ["facturacion@proveedor.com"]
 *   - folder_mappings       → { "Factura": "Facturas Recibidas", ... }
 *   - ai_enabled            → si llamamos a Gemini ante duda
 *   - ai_model              → "gemini-3.5-flash" | "gemini-2.5-pro"
 *
 * Se enchufa desde el módulo de clientes existente (customers/index.js)
 * agregando un botón "Configurar bandeja de email" en cada cliente.
 * ============================================================================
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useFirebase } from 'react-redux-firebase';
import {
  Drawer, Form, Input, Switch, Select, Button, Space,
  message, Divider, Alert, Tag, Typography, Tooltip,
} from 'antd';
import {
  MailOutlined, CopyOutlined, SaveOutlined,
} from '@ant-design/icons';
import { listAllFoldersClient } from './folders_tree';

const { Text, Paragraph } = Typography;

// Dominio donde se reciben los emails entrantes. Por defecto pruebas usa
// `in.crconsultorescolombia.com`. Producción puede sobrescribirlo via
// REACT_APP_INBOUND_DOMAIN en `.env.production` (Create React App lo expone
// como process.env.REACT_APP_*). Debe coincidir con INBOUND_DOMAIN en el
// backend (daysi-cloud-functions/inbound/identify_customer.js y .env.<projectId>).
const INBOUND_DOMAIN = process.env.REACT_APP_INBOUND_DOMAIN || 'in.crconsultorescolombia.com';

const DOCUMENT_TYPES = [
  'Factura', 'Nota credito', 'Nota debito', 'Contrato', 'NDA',
  'Declaracion', 'Poliza', 'Extracto bancario', 'Nomina',
  'Certificado', 'Cotizacion', 'Orden compra',
];

// ─── Componente ─────────────────────────────────────────────────────────────

export const InboundConfigDrawer = ({ customerId, customerName, open, onClose }) => {
  const firebase = useFirebase();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [folders, setFolders] = useState([]);
  const [aliasValue, setAliasValue] = useState('');

  // ─── Cargar config existente + carpetas del cliente ──────────────────────
  useEffect(() => {
    if (!open || !customerId) return;
    setLoading(true);
    Promise.all([
      firebase.firestore()
        .collection('customers').doc(customerId)
        .collection('inbound_config').doc('settings').get(),
      listAllFoldersClient(firebase.firestore(), customerId),
    ])
      .then(([configSnap, tree]) => {
        const config = configSnap.exists ? configSnap.data() : {};
        setFolders(tree);

        // Convertir folder_mappings de objeto a array para el form
        const mappingsArray = Object.entries(config.folder_mappings || {}).map(
          ([documentType, folderName]) => ({ documentType, folderName })
        );

        form.setFieldsValue({
          enabled:           config.enabled !== false,
          alias:             config.alias || generateDefaultAlias(customerName),
          domain_whitelist:  config.domain_whitelist || [],
          sender_whitelist:  config.sender_whitelist || [],
          folder_mappings:   mappingsArray,
        });
        setAliasValue(config.alias || generateDefaultAlias(customerName));
      })
      .catch((err) => {
        console.error('[InboundConfigDrawer] Error cargando config:', err);
        message.error('No se pudo cargar la configuración');
      })
      .finally(() => setLoading(false));
  }, [open, customerId, customerName, firebase, form]);

  // ─── Guardar ─────────────────────────────────────────────────────────────
  const onSave = useCallback(async (values) => {
    setSaving(true);
    try {
      // Convertir folder_mappings de array a objeto para Firestore
      const mappingsObj = {};
      (values.folder_mappings || []).forEach((m) => {
        if (m.documentType && m.folderName) mappingsObj[m.documentType] = m.folderName;
      });

      // Normalizar listas (lowercase, trim)
      const norm = (arr) => (arr || []).map((s) => s.toLowerCase().trim()).filter(Boolean);

      const ref = firebase.firestore()
        .collection('customers').doc(customerId)
        .collection('inbound_config').doc('settings');

      await ref.set({
        enabled:          values.enabled,
        alias:            (values.alias || '').toLowerCase().trim(),
        domain_whitelist: norm(values.domain_whitelist),
        sender_whitelist: norm(values.sender_whitelist),
        folder_mappings:  mappingsObj,
        updatedAt:        firebase.firestore.FieldValue.serverTimestamp(),
        updatedBy:        firebase.auth().currentUser?.uid || null,
      }, { merge: true });

      // Audit
      await firebase.firestore().collection('audit').add({
        uid:       firebase.auth().currentUser?.uid || null,
        eventType: 'INBOUND_CONFIG_UPDATED',
        eventDate: firebase.firestore.FieldValue.serverTimestamp(),
        customerId,
      });

      message.success('Configuración guardada');
      onClose();
    } catch (err) {
      console.error('[InboundConfigDrawer] Error guardando:', err);
      message.error(`No se pudo guardar: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }, [firebase, customerId, onClose]);

  const fullAddress = aliasValue ? `${aliasValue}@${INBOUND_DOMAIN}` : '';

  const copyAddress = () => {
    if (!fullAddress) return;
    navigator.clipboard.writeText(fullAddress)
      .then(() => message.success('Dirección copiada'))
      .catch(() => {});
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <Drawer
      title={<Space><MailOutlined /> Configuración de bandeja de email</Space>}
      width={680}
      open={open}
      onClose={onClose}
      extra={
        <Button type="primary" icon={<SaveOutlined />} loading={saving}
          onClick={() => form.submit()}>
          Guardar
        </Button>
      }
    >
      <Form form={form} layout="vertical" onFinish={onSave} disabled={loading}>

        <Form.Item label="Bandeja activa" name="enabled" valuePropName="checked">
          <Switch checkedChildren="Activa" unCheckedChildren="Inactiva" />
        </Form.Item>

        <Divider orientation="left">Dirección de email</Divider>

        <Alert
          message="Esta es la dirección que el cliente y sus proveedores deben usar para enviar documentos."
          type="info" showIcon style={{ marginBottom: 16 }}
        />

        <Form.Item
          label="Alias"
          name="alias"
          rules={[
            { required: true, message: 'El alias es obligatorio' },
            { pattern: /^[a-z0-9][a-z0-9-_]{2,}$/, message: 'Solo minúsculas, dígitos, guion y underscore' },
          ]}
          extra={
            fullAddress && (
              <Space>
                <Text strong>📬 Dirección completa:</Text>
                <Tag color="blue">{fullAddress}</Tag>
                <Tooltip title="Copiar al portapapeles">
                  <Button size="small" icon={<CopyOutlined />} onClick={copyAddress} />
                </Tooltip>
              </Space>
            )
          }
        >
          <Input
            addonAfter={`@${INBOUND_DOMAIN}`}
            placeholder="acme-7x3"
            onChange={(e) => setAliasValue(e.target.value.toLowerCase().trim())}
          />
        </Form.Item>

        <Divider orientation="left">Remitentes autorizados</Divider>

        <Paragraph type="secondary" style={{ fontSize: 13 }}>
          Si configuras al menos uno, SOLO se aceptarán emails de esos remitentes.
          Si dejas todo vacío, se aceptarán emails de cualquier remitente (no recomendado).
        </Paragraph>

        <Form.Item
          label="Dominios permitidos"
          name="domain_whitelist"
          extra="Acepta todos los emails que vengan de estos dominios"
        >
          <Select mode="tags" placeholder="ej: acme.com" tokenSeparators={[',', ' ']} />
        </Form.Item>

        <Form.Item
          label="Emails específicos permitidos"
          name="sender_whitelist"
          extra="Acepta solo estos emails exactos (útil para proveedores específicos)"
        >
          <Select mode="tags" placeholder="ej: facturacion@proveedor.com" tokenSeparators={[',', ' ']} />
        </Form.Item>

        <Divider orientation="left">Mapeo de tipos → carpetas</Divider>

        <Paragraph type="secondary" style={{ fontSize: 13 }}>
          Configura qué carpeta del cliente recibe cada tipo de documento.
          Si no configuras un tipo, el sistema lo busca por nombre de carpeta.
        </Paragraph>

        <Form.List name="folder_mappings">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => (
                <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                  <Form.Item {...field} name={[field.name, 'documentType']} style={{ marginBottom: 0 }}>
                    <Select placeholder="Tipo de documento" style={{ width: 180 }}
                      options={DOCUMENT_TYPES.map((t) => ({ value: t, label: t }))} />
                  </Form.Item>
                  <Text>→</Text>
                  <Form.Item {...field} name={[field.name, 'folderName']} style={{ marginBottom: 0 }}>
                    <Select placeholder="Carpeta destino" style={{ width: 240 }}
                      options={folders.map((f) => ({ value: f.displayPath, label: f.displayPath }))} />
                  </Form.Item>
                  <Button danger size="small" onClick={() => remove(field.name)}>Quitar</Button>
                </Space>
              ))}
              <Button type="dashed" onClick={() => add()} block>+ Añadir mapeo</Button>
            </>
          )}
        </Form.List>

        <Divider />

        <Alert
          message="Cómo funciona"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Los documentos con confianza ≥ 85% se archivan automáticamente.</li>
              <li>Los demás van a la bandeja de pendientes para revisión humana.</li>
              <li>Cada corrección que hagas mejora la clasificación futura del mismo remitente.</li>
              <li>La clasificación con IA está gestionada por Daysi y siempre activa.</li>
            </ul>
          }
          type="info" showIcon
        />
      </Form>
    </Drawer>
  );
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const generateDefaultAlias = (customerName) => {
  if (!customerName) return '';
  const base = customerName
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')   // quita acentos
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 20);
  // Sufijo random de 3 chars para evitar colisiones
  const suffix = Math.random().toString(36).slice(2, 5);
  return `${base}-${suffix}`;
};

export default InboundConfigDrawer;
